import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const TermsAndConditions = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get current date for the "Last updated" field
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      
      <div className="flex-grow pt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
          <p className="text-gray-600 mb-6"><strong>Last updated:</strong> {currentDate}</p>

          <p className="mb-6">Welcome to Link Spaghetti, owned and operated by Bit Mentors Inc., a company registered in Ottawa, Canada. By accessing or using our website and services ("Service"), you agree to be bound by these Terms and Conditions ("Terms"). Please read them carefully.</p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">1. Eligibility</h2>
          <p className="mb-6">You must be at least 18 years old to use the Service. By using Link Spaghetti, you represent and warrant that you meet this age requirement.</p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">2. Use of Service</h2>
          <p className="mb-6">Link Spaghetti allows you to store and manage your links, profile information including your name, email address, profile photo, and banner photo.</p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">3. User Content</h2>
          <p className="mb-4">You retain all rights, title, and interest in any images or content you upload ("User Content").</p>
          <p className="mb-4">By uploading User Content, you grant Bit Mentors Inc. a non-exclusive, worldwide, royalty-free license to use, host, store, reproduce, and display such content for the purpose of operating and improving the Service.</p>
          <p className="mb-4">You are solely responsible for the User Content you upload. Bit Mentors Inc. does not review, endorse, or take responsibility for any User Content uploaded by users.</p>
          <p className="mb-6">You agree not to upload content that is unlawful, harmful, infringing, defamatory, or otherwise objectionable. We reserve the right to remove any User Content that violates these Terms or applicable laws.</p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">4. Content Moderation and Removal</h2>
          <p className="mb-6">Bit Mentors Inc. reserves the right to remove any user content that violates these Terms, infringes on third-party rights, or is deemed inappropriate at our sole discretion. Users may report content by contacting us at support@linkspaghetti.com.</p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">5. Data Collection and Privacy</h2>
          <p className="mb-6">We collect and store your links, name, email address, profile photos, and banner photos in accordance with our <Link to="/privacy" className="text-purple-600 hover:text-purple-800">Privacy Policy</Link>. We use Google Analytics for website analytics and Stripe to process payments securely.</p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">6. Payments</h2>
          <p className="mb-6">Payments processed via Stripe are subject to Stripe's terms and privacy policies. We do not store your payment information. You agree to pay any applicable fees for premium services as indicated.</p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">7. Intellectual Property</h2>
          <p className="mb-6">All intellectual property rights related to the Service and website, excluding User Content, are owned by Bit Mentors Inc. or its licensors.</p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">8. User Conduct</h2>
          <p className="mb-4">You agree to use the Service lawfully and not to:</p>
          <ul className="list-disc pl-8 mb-6 space-y-2">
            <li>Violate any applicable laws or regulations in Canada, Ireland, the European Union, India, or any other jurisdiction.</li>
            <li>Upload content that infringes third-party rights.</li>
            <li>Use the Service to harm, harass, or defraud others.</li>
            <li>Distribute malicious software or engage in hacking activities.</li>
            <li>Harass, threaten, or defame other users.</li>
            <li>Engage in fraudulent activities or misrepresent your identity.</li>
          </ul>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">9. Third-Party Links and Services</h2>
          <p className="mb-6">Link Spaghetti may contain links to third-party websites. Bit Mentors Inc. does not control and is not responsible for the content, privacy policies, or practices of any third-party sites. Users access third-party links at their own risk.</p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">10. Indemnification</h2>
          <p className="mb-6">Users agree to indemnify and hold harmless Bit Mentors Inc., its affiliates, and employees from any claims, damages, or expenses arising from their use of the Service or violation of these Terms.</p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">11. Disclaimers and Limitation of Liability</h2>
          <p className="mb-4">The Service is provided "as is" without warranties of any kind.</p>
          <p className="mb-4">Bit Mentors Inc. is not responsible or liable for any User Content or any damages arising from use or inability to use the Service.</p>
          <p className="mb-6">We do not guarantee uptime, accuracy, or completeness of the Service.</p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">12. Termination</h2>
          <p className="mb-6">We may suspend or terminate your account for violations of these Terms or for any reason at our discretion.</p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">13. Changes to Terms</h2>
          <p className="mb-6">We reserve the right to update these Terms at any time. Continued use of the Service after changes constitutes acceptance of the updated Terms.</p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">14. International Compliance</h2>
          <p className="mb-6">Users are responsible for ensuring their use of the Service complies with local laws and regulations applicable in their jurisdiction.</p>

          <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">15. Governing Law</h2>
          <p className="mb-6">These Terms are governed by the laws of Canada (Ontario) and applicable international laws in Ireland, Europe, and India where relevant.</p>

          <div className="mt-12 pt-6 border-t border-gray-200 text-center text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Bit Mentors Inc. All rights reserved.
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default TermsAndConditions;
